import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    IconButton,
    Card,
    CardContent,
    Backdrop,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Divider,
    Stack,
} from "@mui/material";
import {
    ArrowBack,
    CreditCard,
    CheckCircle,
    Delete,
    Add,
    Close,
    StarRounded,
} from "@mui/icons-material";
import Fab from "@mui/material/Fab";
import axios from "axios";
import { t } from "i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    getLocalStorageMenuItem,
    setMobileBackColor,
    setMobileButttonBackColor,
    setMobileCardBorderColor,
    setMobileCardColor,
    setMobileTextColor,
    setMobileTextField_BK_FONT_Border_StyleColor,
} from "../utility";
import Toast from "../common/toast";

const SavedCards = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();

    // State management
    const [myCards, setMyCards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [toast, setToast] = useState({ open: false, message: "", success: true });
    const [showAddCardDialog, setShowAddCardDialog] = useState(false);
    const [initializingPaymob, setInitializingPaymob] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, cardId: null, cardPan: "" });

    const token = getLocalStorageMenuItem("mobileLoginToken");
    const language = getLocalStorageMenuItem("lang");

    const queryString = window.location.search;
    const urlParam = new URLSearchParams(queryString);
    const { qrName, qrCode } = useParams();

    const fetchCards = async () => {
        if (!token) {
            openToast(t("Please login to view saved cards"), false);
            return;
        }

        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_API}/customer/payment-methods`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data.success) {
                setMyCards(response.data.data.payment_methods || []);
            }
        } catch (error) {
            console.error("Error fetching payment methods:", error);
            openToast(t("Failed to load payment methods"), false);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Set a payment method as default
     */
    const setDefaultCard = async (paymentMethodId) => {
        try {
            setActionLoading(true);
            await axios.post(
                `${process.env.REACT_APP_API}/customer/payment-methods/${paymentMethodId}/default`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            // Refresh the payment methods list
            await fetchCards();
            openToast(t("Default card updated successfully"), true);
        } catch (error) {
            console.error("Error setting default card:", error);
            openToast(t("Failed to update default card"), false);
        } finally {
            setActionLoading(false);
        }
    };


    const deleteCard = async (paymentMethodId) => {
        try {
            setActionLoading(true);
            await axios.delete(
                `${process.env.REACT_APP_API}/customer/payment-methods/${paymentMethodId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            // Refresh the payment methods list
            await fetchCards();
            openToast(t("Card deleted successfully"), true);
            setDeleteConfirm({ open: false, cardId: null, cardPan: "" });
        } catch (error) {
            console.error("Error deleting card:", error);
            openToast(t("Failed to delete card"), false);
        } finally {
            setActionLoading(false);
        }
    };

    /**
     * Initialize Paymob Pixel for adding a new card
     */
    const initSaveCardPixel = async () => {
        if (!window.Pixel) {
            openToast(t("Payment system not loaded. Please refresh the page."), false);
            return;
        }

        try {
            setInitializingPaymob(true);

            const response = await axios.post(
                `${process.env.REACT_APP_API}/customer/save-payment-method-intention`,
                {
                    menu_code: qrCode,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const { public_key, client_secret } = response.data.data;

            // Clear the container before initializing
            const container = document.getElementById("paymob-save-card-container");
            if (container) {
                container.innerHTML = "";
            }

            // Initialize Paymob Pixel
            new window.Pixel({
                publicKey: public_key,
                clientSecret: client_secret,
                elementId: "paymob-save-card-container",
                paymentMethods: ["card"],
                forceSaveCard: true,
                onSuccess: (res) => {
                    console.log("Payment method saved successfully:", res);

                    // Refresh the payment methods list
                    fetchCards();
                    setShowAddCardDialog(false);
                    openToast(t("Card added successfully"), true);
                },
                onError: (error) => {
                    console.error("Payment error:", error);
                    openToast(t("Failed to add card"), false);
                },
            });
        } catch (error) {
            console.error("Error initializing payment:", error);
            openToast(t("Failed to initialize payment system"), false);
        } finally {
            setInitializingPaymob(false);
        }
    };

    // ---------------- Effects ----------------

    useEffect(() => {
        // Check if user is logged in
        if (!token) {
            openToast(t("Please login to view saved cards"), false);
            setTimeout(() => {
                navigate(`/${qrName}/${qrCode}/menus?lang=${language}&t=${params.get("t")}`);
            }, 2000);
            return;
        }

        fetchCards();
    }, []);

    useEffect(() => {
        // Initialize Paymob when dialog opens
        if (showAddCardDialog) {
            setTimeout(initSaveCardPixel, 200);
        }
    }, [showAddCardDialog]);

    // ---------------- UI Helper Functions ----------------

    const openToast = (message, success) => {
        setToast({ open: true, message, success });
    };

    const closeToast = () => {
        setToast({ open: false, message: "", success: true });
    };

    const handleBack = () => {
        navigate(
            `/${qrName}/${qrCode}/sections?section=${urlParam.get("section")}&t=${urlParam.get("t")}&lang=${getLocalStorageMenuItem("lang")}`
        );
    };

    // const handleBack = () => {
    //     navigate(-1);
    // };

    const handleOpenDeleteConfirm = (cardId, cardPan) => {
        setDeleteConfirm({ open: true, cardId, cardPan });
    };

    const handleCloseDeleteConfirm = () => {
        setDeleteConfirm({ open: false, cardId: null, cardPan: "" });
    };

    const getCardBrandIcon = (cardType) => {
        const type = cardType?.toLowerCase() || "";

        // Return appropriate card brand styling
        const brandColors = {
            visa: "#1A1F71",
            mastercard: "#EB001B",
            amex: "#006FCF",
            default: setMobileButttonBackColor(),
        };

        const color = brandColors[type] || brandColors.default;

        return <CreditCard sx={{ fontSize: 32, color }} />;
    };

    const formatCardNumber = (pan) => {
        if (!pan) return "•••• •••• •••• ••••";
        return pan;
    };

    // Dialog styles
    const dialogStyles = {
        backgroundColor: setMobileTextField_BK_FONT_Border_StyleColor()[0],
        color: setMobileTextField_BK_FONT_Border_StyleColor()[1],
        borderColor: setMobileTextField_BK_FONT_Border_StyleColor()[2],
    };

    // ---------------- Render ----------------

    return (
        <Box
            sx={{
                backgroundColor: setMobileBackColor(),
                minHeight: "100vh",
                pb: 4,
            }}
        >
            {/* Backdrop for action loading */}
            <Backdrop
                open={actionLoading}
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            {/* Header */}
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: setMobileCardColor(),
                    borderBottom: `1px solid ${setMobileCardBorderColor()}`,
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
            >
                <IconButton onClick={handleBack} sx={{ color: setMobileTextColor() }}>
                    <ArrowBack />
                </IconButton>
                <Typography
                    variant="h6"
                    sx={{
                        color: setMobileTextColor(),
                        fontWeight: 600,
                        flex: 1,
                    }}
                    className="head-font"
                >
                    {t("My Cards")}
                </Typography>
                <Chip
                    label={myCards.length}
                    size="small"
                    sx={{
                        backgroundColor: setMobileButttonBackColor(),
                        color: "#fff",
                        fontWeight: 600,
                    }}
                />
            </Box>

            {/* Main Content */}
            <Box sx={{ p: 2.5, pb: 10 }}>
                {/* Loading State */}
                {loading ? (
                    <Box sx={{ textAlign: "center", mt: 8 }}>
                        <CircularProgress sx={{ color: setMobileButttonBackColor() }} />
                        <Typography
                            sx={{ mt: 2, color: setMobileTextColor(), opacity: 0.7 }}
                            className="sub-font"
                        >
                            {t("Loading your cards...")}
                        </Typography>
                    </Box>
                ) : myCards.length === 0 ? (
                    // Empty State
                    <Box sx={{ textAlign: "center", mt: 10, px: 3 }}>
                        <Box
                            sx={{
                                width: 120,
                                height: 120,
                                borderRadius: "50%",
                                backgroundColor: `${setMobileButttonBackColor()}15`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto",
                                mb: 3,
                            }}
                        >
                            <CreditCard
                                sx={{
                                    fontSize: 60,
                                    color: setMobileButttonBackColor(),
                                }}
                            />
                        </Box>
                        <Typography
                            variant="h6"
                            sx={{
                                color: setMobileTextColor(),
                                fontWeight: 600,
                                mb: 1,
                            }}
                            className="head-font"
                        >
                            {t("No saved cards yet")}
                        </Typography>
                        <Typography
                            sx={{
                                color: setMobileTextColor(),
                                opacity: 0.6,
                                mb: 4,
                                maxWidth: 300,
                                mx: "auto",
                            }}
                            className="sub-font"
                        >
                            {t("Add your first payment card to make checkout faster and easier")}
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<Add />}
                            sx={{
                                backgroundColor: setMobileButttonBackColor(),
                                color: "#fff",
                                px: 4,
                                py: 1.5,
                                borderRadius: 3,
                                textTransform: "none",
                                fontSize: "1rem",
                                boxShadow: `0 4px 12px ${setMobileButttonBackColor()}40`,
                                "&:hover": {
                                    backgroundColor: setMobileButttonBackColor(),
                                    opacity: 0.9,
                                },
                            }}
                            onClick={() => setShowAddCardDialog(true)}
                            className="sub-font"
                        >
                            {t("Add Card")}
                        </Button>
                    </Box>
                ) : (
                    // Cards List
                    <>
                        <Stack spacing={2}>
                            {myCards.map((card, index) => (
                                <Card
                                    key={card.id}
                                    sx={{
                                        backgroundColor: setMobileCardColor(),
                                        border: `2px solid ${card.is_default ? setMobileButttonBackColor() : setMobileCardBorderColor()}`,
                                        borderRadius: 3,
                                        position: "relative",
                                        overflow: "visible",
                                        transition: "transform 0.2s, box-shadow 0.2s",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: `0 4px 12px ${setMobileButttonBackColor()}30`,
                                        },
                                    }}
                                >
                                    {/* Default Badge */}
                                    {card.is_default && (
                                        <Chip
                                            icon={<StarRounded sx={{ fontSize: 16 }} />}
                                            label={t("Default")}
                                            size="small"
                                            sx={{
                                                position: "absolute",
                                                top: -12,
                                                right: 16,
                                                backgroundColor: setMobileButttonBackColor(),
                                                color: "#fff",
                                                fontWeight: 600,
                                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                            }}
                                            className="sub-font"
                                        />
                                    )}

                                    <CardContent sx={{ p: 2.5 }}>
                                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                                            {/* Card Brand Icon */}
                                            <Box
                                                sx={{
                                                    width: 56,
                                                    height: 56,
                                                    borderRadius: 2,
                                                    backgroundColor: `${setMobileButttonBackColor()}10`,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {getCardBrandIcon(card.type)}
                                            </Box>

                                            {/* Card Details */}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        color: setMobileTextColor(),
                                                        fontWeight: 600,
                                                        mb: 0.5,
                                                        textTransform: "capitalize",
                                                    }}
                                                    className="head-font"
                                                >
                                                    {card.type || t("Credit Card")}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: setMobileTextColor(),
                                                        opacity: 0.7,
                                                        letterSpacing: 1,
                                                        fontFamily: "monospace",
                                                    }}
                                                    className="sub-font"
                                                >
                                                    {formatCardNumber(card.pan)}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Divider sx={{ my: 2, borderColor: setMobileCardBorderColor() }} />

                                        {/* Action Buttons */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 1,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            {!card.is_default && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<CheckCircle sx={{ fontSize: 18 }} />}
                                                    onClick={() => setDefaultCard(card.id)}
                                                    sx={{
                                                        borderColor: setMobileButttonBackColor(),
                                                        color: setMobileButttonBackColor(),
                                                        borderRadius: 2,
                                                        textTransform: "none",
                                                        flex: 1,
                                                        minWidth: "140px",
                                                        "&:hover": {
                                                            borderColor: setMobileButttonBackColor(),
                                                            backgroundColor: `${setMobileButttonBackColor()}15`,
                                                        },
                                                    }}
                                                    className="sub-font"
                                                >
                                                    {t("Set as Default")}
                                                </Button>
                                            )}

                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<Delete sx={{ fontSize: 18 }} />}
                                                onClick={() => handleOpenDeleteConfirm(card.id, card.pan)}
                                                sx={{
                                                    backgroundColor: "#ff4444",
                                                    color: "#fff",
                                                    borderRadius: 2,
                                                    textTransform: "none",
                                                    flex: card.is_default ? 1 : 0,
                                                    minWidth: "100px",
                                                    "&:hover": {
                                                        backgroundColor: "#dd3333",
                                                    },
                                                }}
                                                className="sub-font"
                                            >
                                                {t("Delete")}
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    </>
                )}
            </Box>

            {/* Floating Action Button - Add Card */}
            <Fab
                color="primary"
                aria-label="add"
                onClick={() => setShowAddCardDialog(true)}
                sx={{
                    position: "fixed",
                    bottom: 24,
                    right: 24,
                    backgroundColor: setMobileButttonBackColor(),
                    color: "#fff",
                    width: 64,
                    height: 64,
                    boxShadow: `0 4px 20px ${setMobileButttonBackColor()}60`,
                    "&:hover": {
                        backgroundColor: setMobileButttonBackColor(),
                        opacity: 0.9,
                        transform: "scale(1.1)",
                    },
                    transition: "all 0.3s ease",
                    zIndex: 1000,
                }}
            >
                <Add sx={{ fontSize: 32 }} />
            </Fab>

            {/* Add Card Dialog */}
            <Dialog
                open={showAddCardDialog}
                onClose={() => setShowAddCardDialog(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    style: {
                        borderRadius: 16,
                        backgroundColor: dialogStyles.backgroundColor,
                        border: `1px solid ${setMobileCardBorderColor()}`,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pb: 1,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CreditCard sx={{ color: setMobileButttonBackColor() }} />
                        <Typography
                            variant="h6"
                            sx={{ color: dialogStyles.color, fontWeight: 600 }}
                            className="head-font"
                        >
                            {t("Add New Card")}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={() => setShowAddCardDialog(false)}
                        sx={{ color: dialogStyles.color }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ px: 3, py: 2 }}>
                    {initializingPaymob ? (
                        <Box sx={{ textAlign: "center", py: 8 }}>
                            <CircularProgress sx={{ color: setMobileButttonBackColor() }} />
                            <Typography
                                sx={{ mt: 2, color: dialogStyles.color, opacity: 0.7 }}
                                className="sub-font"
                            >
                                {t("Initializing payment system...")}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography
                                sx={{
                                    color: dialogStyles.color,
                                    opacity: 0.8,
                                    mb: 2,
                                    fontSize: "0.9rem",
                                }}
                                className="sub-font"
                            >
                                {t("Enter your card details below. Your information is secure and encrypted.")}
                            </Typography>

                            <Box
                                sx={{
                                    border: `1px solid ${setMobileCardBorderColor()}`,
                                    borderRadius: 2,
                                    backgroundColor: "#fff",
                                    minHeight: 420,
                                    p: 2,
                                }}
                            >
                                <Box
                                    id="paymob-save-card-container"
                                    sx={{
                                        minHeight: 400,
                                    }}
                                />
                            </Box>
                        </>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={() => setShowAddCardDialog(false)}
                        sx={{
                            color: dialogStyles.color,
                            textTransform: "none",
                        }}
                        className="sub-font"
                    >
                        {t("Cancel")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirm.open}
                onClose={handleCloseDeleteConfirm}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    style: {
                        borderRadius: 16,
                        backgroundColor: dialogStyles.backgroundColor,
                        border: `1px solid ${setMobileCardBorderColor()}`,
                    },
                }}
            >
                <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            backgroundColor: "rgba(255, 68, 68, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto",
                            mb: 2,
                        }}
                    >
                        <Delete sx={{ fontSize: 32, color: "#ff4444" }} />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{ color: dialogStyles.color, fontWeight: 600 }}
                        className="head-font"
                    >
                        {t("Delete Card")}
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ textAlign: "center", px: 3 }}>
                    <Typography sx={{ color: dialogStyles.color, opacity: 0.8 }} className="sub-font">
                        {t("Are you sure you want to delete this card?")}
                    </Typography>
                    <Typography
                        sx={{
                            color: dialogStyles.color,
                            fontWeight: 600,
                            mt: 1,
                            fontFamily: "monospace",
                        }}
                        className="sub-font"
                    >
                        {deleteConfirm.cardPan}
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <Button
                        onClick={handleCloseDeleteConfirm}
                        variant="outlined"
                        fullWidth
                        sx={{
                            borderColor: setMobileCardBorderColor(),
                            color: dialogStyles.color,
                            borderRadius: 2,
                            textTransform: "none",
                        }}
                        className="sub-font"
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={() => deleteCard(deleteConfirm.cardId)}
                        variant="contained"
                        fullWidth
                        sx={{
                            backgroundColor: "#ff4444",
                            color: "#fff",
                            borderRadius: 2,
                            textTransform: "none",
                            "&:hover": {
                                backgroundColor: "#ff4444",
                                opacity: 0.9,
                            },
                        }}
                        className="sub-font"
                    >
                        {t("Delete")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Toast Notification */}
            <Toast
                open={toast.open}
                message={toast.message}
                success={toast.success}
                resetToast={closeToast}
            />
        </Box>
    );
};

export default SavedCards;